import { scrollToElementById } from "@utils/ui";
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  InstructorDetail,
  InstructorEducationalBackground,
} from "@features/instructor/types";
import type { HTMLReactParserOptions } from "html-react-parser";
import useIntersection from "@shared/hooks/useIntersection";
import { useLayoutContext } from "@context/layout";
import { parseInstructorHtml } from "../helpers/instructorContentParser";
import MobileSkillsPanel from "../components/MobileSkillPanel";
import MobileResearchPanel from "../components/MobileResearchPanel";
import MobileEducationPanel from "../components/MobileEducationPanel";
import MobileAboutPanel from "../components/MobileAboutPanel";
import InstructorProduct from "../components/InstructorProduct";
import { toFarsiNumber } from "@utils/digits";
import { NewExploreProduct } from "types/api/new-explore";
import { SCROLL_OFFSET_ADMIN, SCROLL_OFFSET_USER } from "../helpers/constants";

type InstructorTab = {
  id: string;
  name: string;
  node: ReactNode;
};

const useMobileInstructorHeader = ({
  instructor,
  options,
  productCount,
  products,
  degrees,
}: {
  instructor: InstructorDetail;
  options: HTMLReactParserOptions;
  productCount: number;
  products: NewExploreProduct[] | undefined;
  degrees: InstructorEducationalBackground[];
}) => {
  const refs = useRef<{ offset: number; e: HTMLDivElement | null }[]>([]);
  const [activeId, setActiveId] = useState("about");
  const { isAdmin } = useLayoutContext();

  const { visibleTarget } = useIntersection(refs, 1);
  const scrollOffset = isAdmin ? SCROLL_OFFSET_ADMIN : SCROLL_OFFSET_USER;

  const parsedAbout = useMemo(
    () => parseInstructorHtml(instructor.content || "", options),
    [instructor.content, options]
  );

  const parsedResearch = useMemo(
    () =>
      parseInstructorHtml(instructor.instructor_research_fields || "", options),
    [instructor.instructor_research_fields, options]
  );

  const parsedSkills = useMemo(
    () => parseInstructorHtml(instructor.instructor_skills || "", options),
    [instructor.instructor_skills, options]
  );

  const tabs = useMemo((): InstructorTab[] => {
    const items: InstructorTab[] = [];

    if (instructor.content) {
      items.push({
        id: "about",
        name: "درباره",
        node: (
          <MobileAboutPanel
            instructorName={instructor.name}
            parsedContent={parsedAbout}
          />
        ),
      });
    }

    if (instructor.instructor_educational_background.length) {
      items.push({
        id: "backgroundEducation",
        name: "سوابق تحصیلی",
        node: <MobileEducationPanel degrees={degrees} />,
      });
    }

    if (products?.length) {
      items.push({
        id: "products",
        name: `آموزش‌ها (${toFarsiNumber(productCount)})`,
        node: (
          <InstructorProduct products={products} id={String(instructor.id)} />
        ),
      });
    }

    if (instructor.instructor_research_fields) {
      items.push({
        id: "studyField",
        name: "زمینه های پژوهشی و آموزشی",
        node: <MobileResearchPanel parsedContent={parsedResearch} />,
      });
    }

    if (instructor.instructor_skills) {
      items.push({
        id: "skill",
        name: "مهارت‌ها",
        node: <MobileSkillsPanel parsedContent={parsedSkills} />,
      });
    }

    return items;
  }, [
    instructor,
    degrees,
    products,
    productCount,
    parsedAbout,
    parsedResearch,
    parsedSkills,
  ]);

  useLayoutEffect(() => {
    refs.current = tabs.map((_, index) => ({
      e: refs.current[index]?.e ?? null,
      offset: scrollOffset,
    }));
  }, [tabs, scrollOffset]);

  const handleClickTab = useCallback(
    (id: string) => {
      setActiveId(id);
      scrollToElementById(id, scrollOffset);
    },
    [scrollOffset]
  );

  useEffect(() => {
    setActiveId(visibleTarget || "about");
  }, [visibleTarget]);

  return {
    isAdmin,
    handleClickTab,
    tabs,
    refs,
    scrollOffset,
    activeId,
  };
};

export default useMobileInstructorHeader;
